import * as yup from 'yup';
import {
  AlertCircle, AlertTriangle, Calendar, Check, ChevronRight, Edit, ThumbsDown, ThumbsUp, User, X,
} from 'react-feather';
import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { differenceInDays } from 'date-fns';
import { useContext, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import uniq from 'lodash/uniq';

import { GetOrderSummaryQuery as GetOrderSummary } from '__generated__/graphql';

import { EmbedContext } from '../../Common/EmbedProvider';
import { assetUrl, emailRegex, encodeUrl, getServerErrors } from '../../../common/helpers';
import { useEvent } from '../../Common/EventProvider';
import AddReviewMutation from '../../../common/AddReviewMutation';
import EditEmailForOrderMutation from './EditEmailForOrderMutation';
import EmailInput from '../../../common/ParticipantAttributes/Attributes/EmailInput';
import GetOrderSummaryQuery from '../Tracking/GetOrderSummaryQuery';
import Layout, { SectionTitle } from '../../Common/Layout';
import PlatformFeeLine from '../../../common/Purchases/PlatformFeeLine';
import PurchaseLine from '../../../common/Purchases/PurchaseLine';
import UI from '../../../common/UI';
import useCheckoutUrlState from '../useCheckoutUrlState';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useMutation from '../../../api/useMutation';
import useOrderAcceptedTracking from '../Tracking/useOrderAcceptedTracking';
import useProject from '../../Common/useProject';
import useValidation from '../../../common/useValidation';

type OrderSummary = GetOrderSummary['orderSummary'];

interface SuccessPageProps {
  refetchQueries?: DocumentNode[];
}

const SuccessPage = ({ refetchQueries = [GetOrderSummaryQuery] }: SuccessPageProps) => {
  const { t } = useTranslation();
  const { event } = useEvent();
  const { embedded } = useContext(EmbedContext);
  const history = useHistory();

  const [{ orderId, orderToken }] = useCheckoutUrlState();

  const { data } = useOrderAcceptedTracking({
    event,
    orderId,
    orderToken,
    onError: () => {
      history.push(`/${event.id}`);
    },
  });

  const orderSummary = data?.orderSummary;

  const [emailUpdated, setEmailUpdated] = useState(false);
  const [showEditEmail, setShowEditEmail] = useState(false);

  if (!orderSummary) {
    return (
      <UI.PageLoader />
    );
  }

  const assigneeEmails = uniq([orderSummary.email, ...orderSummary.assignee_emails])
    .filter((email) => email !== orderSummary.email);

  return (
    <Layout event={event} embedded={embedded}>
      {!event.header && <UI.HR />}

      <CheckoutConfirmation orderSummary={orderSummary} />

      <ReviewSection orderSummary={orderSummary} orderToken={orderToken} />

      <UI.HR />

      <UI.GridContainer sc={{ gutter: 0.5 }}>
        <UI.GridContainer sc={{ gutter: 0.25 }}>
          <UI.H2>
            {t('success_page.your_registration')}
          </UI.H2>

          <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <UI.Paragraph sc={{ mb: 0 }}>
              <Trans i18nKey="success_page.confirmation_sent_to" values={{ email: orderSummary.email }}>
                <UI.Strong />
              </Trans>
            </UI.Paragraph>
            {orderSummary.allow_guest_update_email && (
              <UI.Button
                onClick={() => { setEmailUpdated(false); setShowEditEmail(!showEditEmail); }}
                sc={{ outline: true, size: 'sm', noWrap: true, active: showEditEmail }}
              >
                <UI.Icon>
                  {showEditEmail ? <X /> : <Edit />}
                </UI.Icon>
                {' '}
                {t('common:edit')}
              </UI.Button>
            )}
          </UI.FlexContainer>
        </UI.GridContainer>

        {emailUpdated && (
          <UI.FadeIn>
            <UI.Success>
              {t('success_page.email_updated')}
            </UI.Success>
          </UI.FadeIn>
        )}

        {showEditEmail && (
          <UI.FadeIn>
            <EditEmailForm
              orderSummary={orderSummary}
              onSuccess={() => { setEmailUpdated(true); setShowEditEmail(false); }}
              token={orderToken}
              refetchQueries={refetchQueries}
            />
          </UI.FadeIn>
        )}

        {assigneeEmails.length > 0 && (
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('success_page.assignee_dashboard_description')}
          </UI.Paragraph>
        )}

        {orderSummary.unassigned_tickets_count > 0 && (
          <UI.Warning>
            <UI.Strong sc={{ block: true }}>
              {t('unassigned_tickets_warning')}
            </UI.Strong>
            {t('success_page.assign_tickets_from_dashboard_description')}
          </UI.Warning>
        )}
      </UI.GridContainer>

      <OrderSummaryCard orderSummary={orderSummary} />
    </Layout>
  );
};

interface CheckoutConfirmationProps {
  orderSummary: OrderSummary;
}

const CheckoutConfirmation = ({ orderSummary }: CheckoutConfirmationProps) => {
  const { t } = useTranslation();
  const { locale, formatNumber } = useLocale();
  const { event } = useEvent();
  const device = useMediaDevice();

  // Round number to multiples of 10
  const charitiesCount = Math.floor(event.charities_count / 10) * 10;
  const exclusiveCharity = !event.allow_other_charity && event.charities.length === 1 ? event.charities[0] : null;

  const confirmationText = (orderSummary.is_resale && event.resale_confirmation_text)
    ? event.resale_confirmation_text
    : event.confirmation_text;

  return (
    <UI.GridContainer>
      <UI.Div>
        <UI.H2 sc={{ mb: 1, strong: true }}>
          <UI.Icon sc={{ big: true, mr: 1 }}>
            <Check strokeWidth={2.5} />
          </UI.Icon>
          {' '}
          {t('success_page.thank_you', { name: orderSummary.first_name })}
        </UI.H2>
        {confirmationText && <UI.HTML html={confirmationText} />}
        {!confirmationText && t('success_page.default_confirmation_text')}
      </UI.Div>

      <NavContainer sc={{ hasPanels: true }}>
        {event.enable_fundraising && !event.require_fundraising && !orderSummary.created_fundraising_campaign && (
          <UI.LinkBlock
            href={`${event.supporta_url}?locale=${locale}`}
            target="_blank"
            rel="noopener noreferer"
            title={t('success_page.create_fundraiser_for_charity')}
          >
            <UI.GridContainer sc={{ columns: '36px 1fr 16px', gutter: 0.75 }}>
              <UI.Div>
                {exclusiveCharity ? (
                  <img
                    src={exclusiveCharity.logo_url}
                    alt={exclusiveCharity.title}
                    style={{ width: 36 }}
                  />
                ) : (
                  <UI.Badge sc={{ size: 36, mt: 0.75, brand: 'supporta_emerald' }}>
                    <img
                      src={assetUrl('images/logo-supporta-symbol-white.svg')}
                      width={16}
                      alt="Supporta"
                    />
                  </UI.Badge>
                )}
              </UI.Div>
              <UI.Div>
                <UI.Strong>
                  {exclusiveCharity
                    ? t('success_page.create_fundraiser_for_charity_name', { charity: exclusiveCharity.title })
                    : t('success_page.create_fundraiser_for_charity')}
                </UI.Strong>
                <UI.Paragraph sc={{ mb: 0 }}>
                  {t('success_page.create_a_fundraiser_description', { event: event.title })}
                </UI.Paragraph>
                {!exclusiveCharity && (
                  <UI.Div>
                    <UI.FlexContainer>
                      {event.random_charities.slice(0, device.isSmall ? 3 : 4).map((charity) => (
                        <UI.FlexContainer
                          sc={{ mt: 3, pr: [2, 4] }}
                          style={{ height: 30, maxWidth: 90, alignItems: 'center' }}
                          key={charity.id}
                        >
                          <img
                            src={charity.logo_url}
                            alt={charity.title}
                            style={{ objectFit: 'contain', maxWidth: '100%', maxHeight: '100%' }}
                          />
                        </UI.FlexContainer>
                      ))}
                    </UI.FlexContainer>
                    {charitiesCount > 0 && (
                      <UI.Div sc={{ muted: true, small: true, mt: 3 }}>
                        {t('success_page.choose_from_charities', {
                          value: formatNumber(charitiesCount),
                        })}
                      </UI.Div>
                    )}
                  </UI.Div>
                )}
              </UI.Div>
              <UI.Div>
                <NavIcon>
                  <ChevronRight />
                </NavIcon>
              </UI.Div>
            </UI.GridContainer>
          </UI.LinkBlock>
        )}
        <UI.LinkBlock
          href={`/login?${encodeUrl({ email: orderSummary.email })}`}
          target="_blank"
          rel="noreferer noopener"
          title={t('success_page.go_to_dashboard')}
        >
          <UI.GridContainer sc={{ columns: '36px 1fr 16px', gutter: 0.75 }}>
            <UI.Div>
              <UI.Badge sc={{ size: 36, mt: 0.75 }}>
                <UI.Icon sc={{ size: 1.125 }}>
                  <User />
                </UI.Icon>
              </UI.Badge>
            </UI.Div>
            <UI.Div>
              <UI.Strong sc={{ block: true }}>
                {t('success_page.go_to_dashboard')}
              </UI.Strong>
              {t('success_page.go_to_dashboard_description')}
            </UI.Div>
            <UI.Div>
              <NavIcon>
                <ChevronRight />
              </NavIcon>
            </UI.Div>
          </UI.GridContainer>
        </UI.LinkBlock>
        <UI.LinkBlock
          to={`/${event.id}`}
          title={t('success_page.go_to_checkout')}
        >
          <UI.GridContainer sc={{ columns: '36px 1fr 16px', gutter: 0.75 }}>
            <UI.Div>
              <UI.Badge sc={{ size: 36, mt: 0.75 }}>
                <UI.Icon sc={{ size: 1.125 }}>
                  <UI.Icons.Ticket />
                </UI.Icon>
              </UI.Badge>
            </UI.Div>
            <UI.Div>
              <UI.Strong sc={{ block: true }}>
                {t('success_page.go_to_checkout')}
              </UI.Strong>
              {t('success_page.go_to_checkout_description')}
            </UI.Div>
            <UI.Div>
              <NavIcon>
                <ChevronRight />
              </NavIcon>
            </UI.Div>
          </UI.GridContainer>
        </UI.LinkBlock>
      </NavContainer>
    </UI.GridContainer>
  );
};

const NavIcon = styled(UI.Icon)`
  ${({ theme }) => css`
  font-size: 1.2em;
    color: ${theme.colors.gray[400]};
    transition: all 0.1s ease-in-out;
  `}
`;

const NavContainer = styled(UI.Card)`
  ${({ theme }) => css`
    *:hover ${NavIcon} {
      margin-left: 3px;
      color: ${theme.colors.gray[800]};
    }
  `}
`;

interface ReviewSectionProps {
  orderSummary: OrderSummary;
  orderToken: string;
}

const ReviewSection = ({ orderSummary, orderToken }: ReviewSectionProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const [addReview, { loading }] = useMutation(
    AddReviewMutation,
  );

  const [rating, setRating] = useState<number>();
  const [comment, setComment] = useState('');

  const form = useForm({
    values: {
      comment,
    },
  });

  const addPositiveReview = () => {
    if (rating !== 1) {
      setRating(1);

      addReview({
        variables: {
          input: {
            project: { id: project.id },
            order: { id: orderSummary.id },
            rating: 1,
            ...form.values,
            url: window.location.href,
            token: orderToken,
          },
        },
      });
    }
  };

  const addNegativeReview = () => {
    if (rating !== 0) {
      setRating(0);

      addReview({
        variables: {
          input: {
            project: { id: project.id },
            order: { id: orderSummary.id },
            rating: 0,
            ...form.values,
            url: window.location.href,
            token: orderToken,
          },
        },
      });
    }
  };

  const addComment = () => {
    setComment(form.values.comment);

    addReview({
      variables: {
        input: {
          project: { id: project.id },
          order: { id: orderSummary.id },
          rating: 0,
          ...form.values,
          url: window.location.href,
          token: orderToken,
        },
      },
    });
  };

  return (
    <UI.Div>
      <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <UI.Div sc={{ fontWeight: 500, pr: 2 }}>
          {typeof rating === 'undefined' && t('success_page.feedback_title')}
          {rating === 0 && <UI.FadeIn>{t('success_page.feedback_success_message')}</UI.FadeIn>}
          {rating === 1 && <UI.FadeIn>{t('success_page.feedback_success_message')}</UI.FadeIn>}
        </UI.Div>

        <UI.Div sc={{ strong: true, noWrap: true }}>
          <UI.Button
            onClick={() => addPositiveReview()}
            sc={{
              brand: rating === 1 ? 'primary.600' : 'gray.250',
              outline: rating !== 1,
              color: rating !== 1 ? 'primary.600' : undefined,
              muted: rating === 0,
              mr: 1,
            }}
            aria-label={t('success_page.thumbs_up')}
            style={{ boxShadow: 'none' }}
          >
            <UI.Icon>
              <ThumbsUp />
            </UI.Icon>
          </UI.Button>
          <UI.Button
            onClick={() => addNegativeReview()}
            sc={{
              brand: rating === 0 ? 'error' : 'gray.250',
              outline: rating !== 0,
              color: rating !== 0 ? 'error' : undefined,
              muted: rating === 1,
            }}
            aria-label={t('success_page.thumbs_down')}
            style={{ boxShadow: 'none' }}
          >
            <UI.Icon>
              <ThumbsDown />
            </UI.Icon>
          </UI.Button>
        </UI.Div>
      </UI.FlexContainer>

      <UI.AnimateHeight isVisible={rating === 0}>
        <UI.Card sc={{ mt: 2 }}>
          <UI.Form onSubmit={() => addComment()}>
            <UI.FormGrid sc={{ gutter: 0.5 }}>
              <UI.InputGroup>
                <UI.InputLabel htmlFor="Comment">
                  {t('success_page.negative_feedback_message')}
                </UI.InputLabel>
                <UI.TextArea {...form.textarea('comment')} id="Comment" />
              </UI.InputGroup>
              <UI.Div>
                <UI.Button
                  type="submit"
                  disabled={!form.values.comment || form.values.comment === comment}
                  sc={{ loading }}
                >
                  {(!comment || form.values.comment !== comment || loading)
                    ? t('success_page.send_feedback')
                    : (
                      <>
                        <UI.Icon>
                          <Check />
                        </UI.Icon>
                        {' '}
                        {t('success_page.feedback_sent')}
                      </>
                    )}
                </UI.Button>
              </UI.Div>
            </UI.FormGrid>
          </UI.Form>
        </UI.Card>
      </UI.AnimateHeight>
    </UI.Div>
  );
};

interface DueDateLabelProps {
  dueDate: string;
}

const DueDateLabel = ({ dueDate }: DueDateLabelProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();

  const parsedDate = parseDate(dueDate, { timezone: 'UTC', format: 'internal_date' });
  const diffInDays = differenceInDays(parsedDate, new Date());
  const formattedDueDate = formatDate(parsedDate, { format: 'display_compact_date' });

  if (diffInDays === 0) {
    return (
      <UI.Label sc={{ background: 'info', color: 'gray.900' }}>
        <UI.Icon>
          <AlertTriangle />
        </UI.Icon>
        {' '}
        {t('success_page.today')}
      </UI.Label>
    );
  }

  if (diffInDays < 0) {
    return (
      <UI.Label sc={{ background: 'error', color: 'white' }}>
        <UI.Icon>
          <AlertCircle />
        </UI.Icon>
        {' '}
        {formattedDueDate}
      </UI.Label>
    );
  }

  return (
    <UI.Label>
      <UI.Icon>
        <Calendar />
      </UI.Icon>
      {' '}
      {formattedDueDate}
    </UI.Label>
  );
};

interface EditEmailFormProps {
  orderSummary: OrderSummary;
  onSuccess: () => void;
  token: string;
  refetchQueries?: DocumentNode[];
}

const EditEmailForm = ({ orderSummary, onSuccess, token, refetchQueries }: EditEmailFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    values: {
      email: orderSummary.email,
    },
  });

  const [editEmail, { loading, error }] = useMutation(
    EditEmailForOrderMutation,
    {
      variables: {
        input: {
          id: orderSummary.id,
          email: form.values.email,
          token,
        },
      },
      refetchQueries,
      onCompleted: () => onSuccess(),
    },
  );

  const editEmailSchema = yup.object().shape({
    email: yup.string().required().matches(emailRegex, { excludeEmptyString: true, name: 'email' }),
  });

  const { valid, errors } = useValidation({
    schema: editEmailSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Card>
      <UI.Form onSubmit={() => editEmail()}>
        <UI.FormGrid>
          <UI.Paragraph>
            {t('success_page.edit_email_description')}
          </UI.Paragraph>

          <UI.ServerErrors error={error} />

          <EmailInput
            value={form.values}
            onChange={(value) => form.set((user) => ({ ...user, ...value }))}
            onBlur={(key) => form.setTouched(key, true)}
            touched={form.touched}
            errors={errors}
            required
          />

          <UI.Button
            type="submit"
            sc={{ brand: 'secondary', loading }}
            disabled={loading || !valid || !form.changed()}
          >
            {t('success_page.edit_email_button')}
          </UI.Button>
        </UI.FormGrid>
      </UI.Form>
    </UI.Card>
  );
};

interface OrderSummaryCardProps {
  orderSummary: OrderSummary;
}

const OrderSummaryCard = ({ orderSummary }: OrderSummaryCardProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <UI.Card>
      <UI.GridContainer sc={{ gutter: 0.75 }}>
        <UI.Div>
          <SectionTitle sc={{ mb: 1 }}>
            {t('success_page.purchases')}
          </SectionTitle>
          {`${t('success_page.order_number')}: ${orderSummary.invoice_id}`}
        </UI.Div>

        <UI.HR />

        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {orderSummary.tickets.map((ticket, index) => (
            <PurchaseLine ticket={ticket} key={index} />
          ))}
          {orderSummary.products.map((product, index) => (
            <PurchaseLine product={product} key={index} />
          ))}
        </UI.GridContainer>

        {orderSummary.passed_on_fee > 0 && (
          <>
            <UI.HR />
            <PlatformFeeLine fee={orderSummary.passed_on_fee} />
          </>
        )}

        <UI.HR sc={{ borderWidth: 2 }} />

        <UI.GridContainer sc={{ gutter: 0.25 }}>
          <UI.FlexContainer sc={{ justifyContent: 'space-between', strong: true }}>
            <UI.Div sc={{ fontSize: 3, fontWeight: 500 }}>
              {t('total')}
            </UI.Div>
            <UI.Div sc={{ fontSize: 3, strong: true, textAlign: 'right' }}>
              {formatCurrency(orderSummary.amount)}
            </UI.Div>
          </UI.FlexContainer>

          {(orderSummary.vat_amount + orderSummary.passed_on_fee_vat_amount) > 0 && (
            <UI.FlexContainer sc={{ justifyContent: 'space-between', muted: true }}>
              <UI.Span>
                {t('success_page.included_taxes')}
              </UI.Span>
              <UI.Span>
                {formatCurrency(orderSummary.vat_amount + orderSummary.passed_on_fee_vat_amount)}
              </UI.Span>
            </UI.FlexContainer>
          )}

          {orderSummary.remaining_amount > 0 && (
            <>
              <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
                <UI.Span>
                  {t('success_page.paid')}
                </UI.Span>
                <UI.Span>
                  {formatCurrency(orderSummary.paid_amount)}
                </UI.Span>
              </UI.FlexContainer>
              <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
                <UI.Span>
                  {t('success_page.remaining')}
                  {' '}
                  <UI.Small>
                    <DueDateLabel dueDate={orderSummary.due_date} />
                  </UI.Small>
                </UI.Span>
                <UI.Span>
                  {formatCurrency(orderSummary.remaining_amount)}
                </UI.Span>
              </UI.FlexContainer>
            </>
          )}
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Card>
  );
};

export default SuccessPage;
